/**
 * useFavorites — React Query hook for restaurant favorites CRUD.
 *
 * Requires authentication. Provides:
 * - favorites: Set of restaurantIds for O(1) lookup
 * - isFavorite(id): check if a restaurant is favorited
 * - toggleFavorite(id): add or remove from favorites
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { apiClient } from '../services/apiClient';
import { FavoritesListResponse } from '../types';

export function useFavorites(isAuthenticated: boolean) {
  const queryClient = useQueryClient();

  const favoritesQuery = useQuery({
    queryKey: ['favorites'],
    queryFn: () => apiClient.get<FavoritesListResponse>('/api/favorites'),
    enabled: isAuthenticated,
  });

  const addMutation = useMutation({
    mutationFn: (restaurantId: string) =>
      apiClient.post<{ restaurantId: string }>('/api/favorites', { restaurantId }),
    onMutate: async (restaurantId) => {
      // Optimistic update
      await queryClient.cancelQueries({ queryKey: ['favorites'] });
      const previous = queryClient.getQueryData<FavoritesListResponse>(['favorites']);

      queryClient.setQueryData<FavoritesListResponse>(['favorites'], (old) => {
        if (!old) return { favorites: [restaurantId], total: 1 };
        if (old.favorites.includes(restaurantId)) return old;
        return { favorites: [...old.favorites, restaurantId], total: old.total + 1 };
      });

      return { previous };
    },
    onError: (_err, _restaurantId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['favorites'], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['favorites'] });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (restaurantId: string) =>
      apiClient.delete<void>(`/api/favorites/${restaurantId}`),
    onMutate: async (restaurantId) => {
      // Optimistic update
      await queryClient.cancelQueries({ queryKey: ['favorites'] });
      const previous = queryClient.getQueryData<FavoritesListResponse>(['favorites']);

      queryClient.setQueryData<FavoritesListResponse>(['favorites'], (old) => {
        if (!old) return { favorites: [], total: 0 };
        const filtered = old.favorites.filter((id) => id !== restaurantId);
        return { favorites: filtered, total: filtered.length };
      });

      return { previous };
    },
    onError: (_err, _restaurantId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['favorites'], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['favorites'] });
    },
  });

  const favoriteIds = useMemo(
    () => new Set(favoritesQuery.data?.favorites ?? []),
    [favoritesQuery.data?.favorites]
  );

  const isFavorite = useCallback(
    (restaurantId: string): boolean => favoriteIds.has(restaurantId),
    [favoriteIds]
  );

  const toggleFavorite = useCallback(
    (restaurantId: string) => {
      if (favoriteIds.has(restaurantId)) {
        removeMutation.mutate(restaurantId);
      } else {
        addMutation.mutate(restaurantId);
      }
    },
    [favoriteIds, addMutation, removeMutation]
  );

  return {
    favoriteIds,
    isFavorite,
    toggleFavorite,
    isLoading: favoritesQuery.isLoading,
    isToggling: addMutation.isPending || removeMutation.isPending,
  };
}
